import React, { useEffect, useState } from 'react';
import { Box, Typography, Grid, CircularProgress, Alert, Card, CardContent, Select, MenuItem, FormControl, InputLabel, Button, ButtonGroup } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
import { getDashboardResumen, IDashboardResumen } from '../services/dashboardService';
import cuentaCorrienteService, { EstadisticasCuentaCorriente } from '../services/cuentaCorrienteService';
import cuentaCorrienteProveedoresService, { EstadisticasCuentaCorrienteProveedores } from '../services/cuentaCorrienteProveedoresService';
import { formatCurrency } from '../utils/formatters';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { ShoppingCart, ShoppingBag, DollarSign, CreditCard, Receipt, FileText, Ticket } from 'lucide-react';

type DashboardParams = {
  mes?: number;
  año?: number;
  trimestre?: number;
};

const NOMBRES_MESES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
];

// Función para determinar el tamaño de fuente según el valor
const getFontSize = (value: number, isSmallScreen: boolean = false) => {
  const absValue = Math.abs(value);
  
  if (absValue >= 1000000) {
    // Valores mayores a 1 millón - fuente más pequeña
    return isSmallScreen 
      ? { xs: '1rem', sm: '1.25rem', md: '1.5rem' }
      : { xs: '1.1rem', sm: '1.4rem', md: '1.6rem' };
  } else if (absValue >= 100000) {
    // Valores entre 100k y 1 millón
    return { xs: '1.3rem', sm: '1.6rem', md: '1.9rem' };
  } else {
    // Valores menores a 100k
    return { xs: '1.5rem', sm: '1.75rem', md: '2rem' };
  }
};

type PeriodoFiltro = 'mesActual' | 'mesAnterior' | 'personalizado';

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [periodoFiltro, setPeriodoFiltro] = useState<PeriodoFiltro>('mesActual');
  const [mesPersonalizado, setMesPersonalizado] = useState<number>(new Date().getMonth() + 1);
  const [añoPersonalizado, setAñoPersonalizado] = useState<number>(new Date().getFullYear());
  
  const [resumen, setResumen] = useState<IDashboardResumen>({
    ventasMes: 0,
    comprasMes: 0,
    cantidadVentas: 0,
    cantidadCompras: 0,
    totalCobradoMes: 0,
    totalPagadoMes: 0,
    diasPromedioPagoCobranzas: undefined,
    diasPromedioPagoCompras: undefined,
    datosMensuales: []
  });
  const [estadisticasClientes, setEstadisticasClientes] = useState<EstadisticasCuentaCorriente>({
    totalClientesConCuentaCorriente: 0,
    saldoTotalGeneral: 0,
    saldoVencidoTotal: 0,
    totalMovimientos: 0,
    ultimaActualizacion: '',
    nroVendedorFiltrado: null,
    usuarioFiltrado: null
  });
  const [estadisticasProveedores, setEstadisticasProveedores] = useState<EstadisticasCuentaCorrienteProveedores>({
    totalProveedoresConCuentaCorriente: 0,
    saldoTotalGeneral: 0,
    saldoVencidoTotal: 0,
    totalMovimientos: 0,
    ultimaActualizacion: ''
  });
  const { enqueueSnackbar } = useSnackbar();

  // Función para obtener el texto del período seleccionado
  const getTextoPeriodo = (): string => {
    const fechaActual = new Date();
    const añoActual = fechaActual.getFullYear();
    const mesActual = fechaActual.getMonth() + 1;

    switch (periodoFiltro) {
      case 'mesActual':
        return `${NOMBRES_MESES[mesActual - 1]} ${añoActual}`;
      case 'mesAnterior': {
        const mesAnterior = mesActual === 1 ? 12 : mesActual - 1;
        const añoAnterior = mesActual === 1 ? añoActual - 1 : añoActual;
        return `${NOMBRES_MESES[mesAnterior - 1]} ${añoAnterior}`;
      }
      case 'personalizado':
        return `${NOMBRES_MESES[mesPersonalizado - 1]} ${añoPersonalizado}`;
      default:
        return '';
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        // Función para obtener los parámetros según el filtro seleccionado
        const fechaActual = new Date();
        const añoActual = fechaActual.getFullYear();
        const mesActual = fechaActual.getMonth() + 1;
        
        let params: DashboardParams = {};
        
        switch (periodoFiltro) {
          case 'mesActual':
            params = { mes: mesActual, año: añoActual };
            break;
          case 'mesAnterior': {
            const mesAnterior = mesActual === 1 ? 12 : mesActual - 1;
            const añoAnterior = mesActual === 1 ? añoActual - 1 : añoActual;
            params = { mes: mesAnterior, año: añoAnterior };
            break;
          }
          case 'personalizado':
            params = { mes: mesPersonalizado, año: añoPersonalizado };
            break;
          default:
            params = {};
        }
        console.log('Iniciando carga de datos del dashboard con filtro:', params);
        const dashboardParams: DashboardParams | undefined = Object.keys(params).length > 0 ? params : undefined;
        const [dashboardData, clientesStats, proveedoresStats] = await Promise.all([
          (getDashboardResumen as (params?: DashboardParams) => Promise<IDashboardResumen>)(dashboardParams),
          cuentaCorrienteService.getEstadisticas(),
          cuentaCorrienteProveedoresService.getEstadisticas()
        ]);
        console.log('Datos recibidos:', dashboardData);
        console.log('Estadísticas clientes:', clientesStats);
        console.log('Estadísticas proveedores:', proveedoresStats);
        setResumen(dashboardData);
        setEstadisticasClientes(clientesStats);
        setEstadisticasProveedores(proveedoresStats);
        setError(null);
      } catch (err: any) {
        console.error('Error al cargar datos:', err);
        setError(err.message || 'Error al cargar los datos del dashboard');
        enqueueSnackbar('Error al cargar los datos del dashboard', { variant: 'error' });
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [enqueueSnackbar, periodoFiltro, mesPersonalizado, añoPersonalizado]);

  if (loading) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
        <Card 
          sx={{ 
            bgcolor: '#ffffff',
            borderRadius: 3,
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          }}
        >
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            <Alert severity="error">{error}</Alert>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card 
        sx={{ 
          bgcolor: '#ffffff',
          borderRadius: 3,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          overflow: 'hidden'
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
            <Typography 
              variant="h4" 
              sx={{ 
                fontWeight: 600,
                color: '#1976d2'
              }}
            >
              Indicadores
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
              <ButtonGroup variant="outlined" size="small" sx={{ flexWrap: 'wrap' }}>
                <Button 
                  variant={periodoFiltro === 'mesActual' ? 'contained' : 'outlined'}
                  onClick={() => setPeriodoFiltro('mesActual')}
                >
                  Mes Actual
                </Button>
                <Button 
                  variant={periodoFiltro === 'mesAnterior' ? 'contained' : 'outlined'}
                  onClick={() => setPeriodoFiltro('mesAnterior')}
                >
                  Mes Anterior
                </Button>
                <Button 
                  variant={periodoFiltro === 'personalizado' ? 'contained' : 'outlined'}
                  onClick={() => setPeriodoFiltro('personalizado')}
                >
                  Personalizado
                </Button>
              </ButtonGroup>
              {periodoFiltro === 'personalizado' && (
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel>Mes</InputLabel>
                    <Select
                      value={mesPersonalizado}
                      label="Mes"
                      onChange={(e) => setMesPersonalizado(Number(e.target.value))}
                    >
                      {NOMBRES_MESES.map((mes, index) => (
                        <MenuItem key={index + 1} value={index + 1}>{mes}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ minWidth: 100 }}>
                    <InputLabel>Año</InputLabel>
                    <Select
                      value={añoPersonalizado}
                      label="Año"
                      onChange={(e) => setAñoPersonalizado(Number(e.target.value))}
                    >
                      {Array.from({ length: 5 }, (_, i) => {
                        const año = new Date().getFullYear() - i;
                        return <MenuItem key={año} value={año}>{año}</MenuItem>;
                      })}
                    </Select>
                  </FormControl>
                </Box>
              )}
            </Box>
          </Box>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={6} lg={6}>
              <Card
                sx={{
                  p: 2.5,
                  display: 'flex',
                  flexDirection: 'column',
                  height: 160,
                  borderRadius: 3,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  background: 'linear-gradient(135deg, #e3f2fd 0%, #ffffff 100%)',
                  border: '1px solid #e0e0e0',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    boxShadow: '0 4px 16px rgba(25, 118, 210, 0.2)',
                    transform: 'translateY(-4px)',
                  }
                }}
              >
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#1976d2' }}>
                    Ventas del Período
                  </Typography>
                  <Box
                    sx={{
                      bgcolor: '#1976d2',
                      borderRadius: 2,
                      p: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <ShoppingCart color="#ffffff" size={24} />
                  </Box>
                </Box>
                <Typography 
                  variant="h4" 
                  sx={{ 
                    fontWeight: 700,
                    color: '#1976d2',
                    mt: 'auto',
                    fontSize: getFontSize(resumen.ventasMes),
                    wordBreak: 'break-word',
                    lineHeight: 1.2
                  }}
                >
                  {formatCurrency(resumen.ventasMes)}
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={6} lg={6}>
              <Card
                sx={{
                  p: 2.5,
                  display: 'flex',
                  flexDirection: 'column',
                  height: 160,
                  borderRadius: 3,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  background: 'linear-gradient(135deg, #e8f5e9 0%, #ffffff 100%)',
                  border: '1px solid #e0e0e0',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    boxShadow: '0 4px 16px rgba(46, 125, 50, 0.2)',
                    transform: 'translateY(-4px)',
                  }
                }}
              >
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#2e7d32' }}>
                    Compras del Período
                  </Typography>
                  <Box
                    sx={{
                      bgcolor: '#2e7d32',
                      borderRadius: 2,
                      p: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <ShoppingBag color="#ffffff" size={24} />
                  </Box>
                </Box>
                <Typography 
                  variant="h4" 
                  sx={{ 
                    fontWeight: 700,
                    color: '#2e7d32',
                    mt: 'auto',
                    fontSize: getFontSize(resumen.comprasMes),
                    wordBreak: 'break-word',
                    lineHeight: 1.2
                  }}
                >
                  {formatCurrency(resumen.comprasMes)}
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={6} lg={6}>
              <Card
                sx={{
                  p: 2.5,
                  display: 'flex',
                  flexDirection: 'column',
                  height: 160,
                  borderRadius: 3,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  background: 'linear-gradient(135deg, #ede7f6 0%, #ffffff 100%)',
                  border: '1px solid #e0e0e0',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    boxShadow: '0 4px 16px rgba(103, 58, 183, 0.2)',
                    transform: 'translateY(-4px)',
                  }
                }}
              >
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#673ab7' }}>
                    Ticket Promedio de Venta
                  </Typography>
                  <Box
                    sx={{
                      bgcolor: '#673ab7',
                      borderRadius: 2,
                      p: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Ticket color="#ffffff" size={24} />
                  </Box>
                </Box>
                <Typography variant="body2" sx={{ color: '#673ab7', opacity: 0.9, mb: 1 }}>
                  {resumen.cantidadVentas || 0} venta{(resumen.cantidadVentas || 0) !== 1 ? 's' : ''} realizada{(resumen.cantidadVentas || 0) !== 1 ? 's' : ''}
                </Typography>
                <Typography 
                  variant="h4" 
                  sx={{ 
                    fontWeight: 700,
                    color: '#673ab7',
                    mt: 'auto',
                    fontSize: getFontSize(
                      resumen.cantidadVentas && resumen.cantidadVentas > 0 
                        ? resumen.ventasMes / resumen.cantidadVentas 
                        : 0
                    ),
                    wordBreak: 'break-word',
                    lineHeight: 1.2
                  }}
                >
                  {resumen.cantidadVentas && resumen.cantidadVentas > 0
                    ? formatCurrency(resumen.ventasMes / resumen.cantidadVentas)
                    : formatCurrency(0)}
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={6} lg={6}>
              <Card
                sx={{
                  p: 2.5,
                  display: 'flex',
                  flexDirection: 'column',
                  height: 160,
                  borderRadius: 3,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  background: 'linear-gradient(135deg, #e8f5e9 0%, #ffffff 100%)',
                  border: '1px solid #e0e0e0',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    boxShadow: '0 4px 16px rgba(46, 125, 50, 0.2)',
                    transform: 'translateY(-4px)',
                  }
                }}
              >
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#2e7d32' }}>
                    Ticket Promedio de Compras
                  </Typography>
                  <Box
                    sx={{
                      bgcolor: '#2e7d32',
                      borderRadius: 2,
                      p: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <ShoppingBag color="#ffffff" size={24} />
                  </Box>
                </Box>
                <Typography variant="body2" sx={{ color: '#2e7d32', opacity: 0.9, mb: 1 }}>
                  {resumen.cantidadCompras || 0} compra{(resumen.cantidadCompras || 0) !== 1 ? 's' : ''} realizada{(resumen.cantidadCompras || 0) !== 1 ? 's' : ''}
                </Typography>
                <Typography 
                  variant="h4" 
                  sx={{ 
                    fontWeight: 700,
                    color: '#2e7d32',
                    mt: 'auto',
                    fontSize: getFontSize(
                      resumen.cantidadCompras && resumen.cantidadCompras > 0 
                        ? resumen.comprasMes / resumen.cantidadCompras 
                        : 0
                    ),
                    wordBreak: 'break-word',
                    lineHeight: 1.2
                  }}
                >
                  {resumen.cantidadCompras && resumen.cantidadCompras > 0
                    ? formatCurrency(resumen.comprasMes / resumen.cantidadCompras)
                    : formatCurrency(0)}
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={6} lg={6}>
              <Card
                onClick={() => navigate('/ventas/informes/cuentas-cobrar')}
                sx={{
                  p: 2.5,
                  display: 'flex',
                  flexDirection: 'column',
                  height: 160,
                  borderRadius: 3,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  background: 'linear-gradient(135deg, #f3e5f5 0%, #ffffff 100%)',
                  border: '1px solid #e0e0e0',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer',
                  '&:hover': {
                    boxShadow: '0 4px 16px rgba(156, 39, 176, 0.2)',
                    transform: 'translateY(-4px)',
                  }
                }}
              >
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#9c27b0' }}>
                    Cuentas por Cobrar
                  </Typography>
                  <Box
                    sx={{
                      bgcolor: '#9c27b0',
                      borderRadius: 2,
                      p: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Receipt color="#ffffff" size={24} />
                  </Box>
                </Box>
                <Typography variant="body2" sx={{ color: '#9c27b0', opacity: 0.9, mb: 1 }}>
                  {estadisticasClientes.totalClientesConCuentaCorriente} cliente{estadisticasClientes.totalClientesConCuentaCorriente !== 1 ? 's' : ''} con saldo
                </Typography>
                <Typography 
                  variant="h4" 
                  sx={{ 
                    fontWeight: 700,
                    color: '#9c27b0',
                    mt: 'auto',
                    fontSize: getFontSize(estadisticasClientes.saldoTotalGeneral, true),
                    wordBreak: 'break-word',
                    lineHeight: 1.2
                  }}
                >
                  {formatCurrency(estadisticasClientes.saldoTotalGeneral)}
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={6} lg={6}>
              <Card
                onClick={() => navigate('/compras/informes/cuentas-pagar')}
                sx={{
                  p: 2.5,
                  display: 'flex',
                  flexDirection: 'column',
                  height: 160,
                  borderRadius: 3,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  background: 'linear-gradient(135deg, #e1f5fe 0%, #ffffff 100%)',
                  border: '1px solid #e0e0e0',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer',
                  '&:hover': {
                    boxShadow: '0 4px 16px rgba(0, 151, 167, 0.2)',
                    transform: 'translateY(-4px)',
                  }
                }}
              >
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#0097a7' }}>
                    Cuentas por Pagar
                  </Typography>
                  <Box
                    sx={{
                      bgcolor: '#0097a7',
                      borderRadius: 2,
                      p: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <FileText color="#ffffff" size={24} />
                  </Box>
                </Box>
                <Typography variant="body2" sx={{ color: '#0097a7', opacity: 0.9, mb: 1 }}>
                  {estadisticasProveedores.totalProveedoresConCuentaCorriente} proveedor{estadisticasProveedores.totalProveedoresConCuentaCorriente !== 1 ? 'es' : ''} con saldo
                </Typography>
                <Typography 
                  variant="h4" 
                  sx={{ 
                    fontWeight: 700,
                    color: '#0097a7',
                    mt: 'auto',
                    fontSize: getFontSize(estadisticasProveedores.saldoTotalGeneral, true),
                    wordBreak: 'break-word',
                    lineHeight: 1.2
                  }}
                >
                  {formatCurrency(estadisticasProveedores.saldoTotalGeneral)}
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={6} lg={6}>
              <Card
                onClick={() => navigate('/tesoreria/recibos')}
                sx={{
                  p: 2.5,
                  display: 'flex',
                  flexDirection: 'column',
                  height: 160,
                  borderRadius: 3,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  background: 'linear-gradient(135deg, #fff3e0 0%, #ffffff 100%)',
                  border: '1px solid #e0e0e0',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer',
                  '&:hover': {
                    boxShadow: '0 4px 16px rgba(237, 108, 2, 0.2)',
                    transform: 'translateY(-4px)',
                  }
                }}
              >
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#ed6c02' }}>
                    Total Cobrado del Período
                  </Typography>
                  <Box
                    sx={{
                      bgcolor: '#ed6c02',
                      borderRadius: 2,
                      p: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <DollarSign color="#ffffff" size={24} />
                  </Box>
                </Box>
                {resumen.diasPromedioPagoCobranzas != null && (
                  <Typography variant="body2" sx={{ color: '#ed6c02', opacity: 0.9, mb: 1 }}>
                    Promedio {Number(resumen.diasPromedioPagoCobranzas).toFixed(1)} días de cobro
                  </Typography>
                )}
                <Typography 
                  variant="h4" 
                  sx={{ 
                    fontWeight: 700,
                    color: '#ed6c02',
                    mt: 'auto',
                    fontSize: getFontSize(resumen.totalCobradoMes),
                    wordBreak: 'break-word',
                    lineHeight: 1.2
                  }}
                >
                  {formatCurrency(resumen.totalCobradoMes)}
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={6} lg={6}>
              <Card
                onClick={() => navigate('/tesoreria/orden-pago')}
                sx={{
                  p: 2.5,
                  display: 'flex',
                  flexDirection: 'column',
                  height: 160,
                  borderRadius: 3,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  background: 'linear-gradient(135deg, #ffebee 0%, #ffffff 100%)',
                  border: '1px solid #e0e0e0',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer',
                  '&:hover': {
                    boxShadow: '0 4px 16px rgba(211, 47, 47, 0.2)',
                    transform: 'translateY(-4px)',
                  }
                }}
              >
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#d32f2f' }}>
                    Total Pagado del Período
                  </Typography>
                  <Box
                    sx={{
                      bgcolor: '#d32f2f',
                      borderRadius: 2,
                      p: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <CreditCard color="#ffffff" size={24} />
                  </Box>
                </Box>
                {resumen.diasPromedioPagoCompras != null && (
                  <Typography variant="body2" sx={{ color: '#d32f2f', opacity: 0.9, mb: 1 }}>
                    Promedio {Number(resumen.diasPromedioPagoCompras).toFixed(1)} días de pago
                  </Typography>
                )}
                <Typography 
                  variant="h4" 
                  sx={{ 
                    fontWeight: 700,
                    color: '#d32f2f',
                    mt: 'auto',
                    fontSize: getFontSize(resumen.totalPagadoMes),
                    wordBreak: 'break-word',
                    lineHeight: 1.2
                  }}
                >
                  {formatCurrency(resumen.totalPagadoMes)}
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={12}>
              <Card
                sx={{
                  p: 3,
                  borderRadius: 3,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  border: '1px solid #e0e0e0',
                }}
              >
                <Typography 
                  variant="h6" 
                  sx={{ 
                    mb: 3,
                    fontWeight: 600,
                    color: '#1976d2'
                  }}
                >
                  Ventas y Compras por Período
                </Typography>
                <Box sx={{ width: '100%', height: 400 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={resumen.datosMensuales.map(d => ({
                        mes: NOMBRES_MESES[d.mes - 1],
                        ventas: d.totalVentas,
                        compras: d.totalCompras
                      }))}
                      margin={{
                        top: 20,
                        right: 30,
                        left: 20,
                        bottom: 5,
                      }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                      <XAxis 
                        dataKey="mes" 
                        tick={{ fill: '#666', fontSize: 12 }}
                        stroke="#999"
                      />
                      <YAxis 
                        tick={{ fill: '#666', fontSize: 12 }}
                        stroke="#999"
                      />
                      <Tooltip 
                        formatter={(value: number) => formatCurrency(value)}
                        labelFormatter={(label: string) => `Mes: ${label}`}
                        contentStyle={{
                          borderRadius: 8,
                          border: '1px solid #e0e0e0',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                        }}
                      />
                      <Legend 
                        wrapperStyle={{ paddingTop: 20 }}
                      />
                      <Bar 
                        dataKey="ventas" 
                        name="Ventas" 
                        fill="#1976d2" 
                        radius={[8, 8, 0, 0]}
                      />
                      <Bar 
                        dataKey="compras" 
                        name="Compras" 
                        fill="#2e7d32" 
                        radius={[8, 8, 0, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </Card>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
};

export default Dashboard; 